import * as React from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from "@/components/ui/alert-dialog";
import { PlanEditorDialog } from "@/components/admin/PlanEditorDialog";
import { useAdminPlans, useDeactivatePlan } from "@/hooks/useAdmin";
import { useToast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";
import type { PlanDto } from "@/types/api";

export function AdminPlansPage() {
  const { data, isLoading } = useAdminPlans();
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<PlanDto | undefined>();
  const deactivate = useDeactivatePlan();
  const { toast } = useToast();

  function openCreate() { setEditing(undefined); setEditorOpen(true); }
  function openEdit(p: PlanDto) { setEditing(p); setEditorOpen(true); }

  async function onDeactivate(slug: string) {
    try {
      await deactivate.mutateAsync(slug);
      toast({ title: "Plan deactivated", variant: "success" });
    } catch (err) {
      toast({ title: "Failed", description: err instanceof ApiError ? err.message : "Network error.", variant: "destructive" });
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-display text-xl font-bold tracking-tight sm:text-2xl">Plans</h2>
          <p className="text-sm text-muted-foreground">Catalog shown on /plans + used during checkout.</p>
        </div>
        <Button onClick={openCreate}><Plus className="h-4 w-4" />New plan</Button>
      </div>

      {isLoading ? (
        <Card><CardContent className="space-y-2 py-5">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
        </CardContent></Card>
      ) : !data || data.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">
          No plans yet. Create one to populate /plans.
        </CardContent></Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plan</TableHead>
                <TableHead className="hidden sm:table-cell">Type</TableHead>
                <TableHead>Price</TableHead>
                <TableHead className="hidden md:table-cell">20i code</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-24" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map(p => (
                <TableRow key={p.slug}>
                  <TableCell>
                    <p className="font-medium">{p.name}</p>
                    <p className="font-mono text-xs text-muted-foreground">{p.slug}</p>
                  </TableCell>
                  <TableCell className="hidden text-sm text-muted-foreground capitalize sm:table-cell">{p.plan_type}</TableCell>
                  <TableCell className="text-sm">${Math.round(p.price_monthly)}<span className="text-muted-foreground">/mo</span></TableCell>
                  <TableCell className="hidden font-mono text-xs text-muted-foreground md:table-cell">{p.twentyi_product_code ?? "—"}</TableCell>
                  <TableCell>
                    {p.active ? <Badge variant="success">Active</Badge>
                              : <Badge variant="outline">Inactive</Badge>}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(p)} aria-label={`Edit ${p.slug}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {p.active && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" aria-label={`Deactivate ${p.slug}`}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Deactivate {p.name}?</AlertDialogTitle>
                              <AlertDialogDescription>
                                It'll disappear from /plans. Existing subscribers keep their plan; you can re-activate via Edit.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => onDeactivate(p.slug)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >Deactivate</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <PlanEditorDialog open={editorOpen} onOpenChange={setEditorOpen} initial={editing} />
    </div>
  );
}
